import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SimplePageLayout } from "../../components/simplePage/simplePageLayout.tsx";
import { GridEditModal } from "../../components/editSearch/gridEditModal.tsx";
import { useToast } from "../../hooks/useToast.ts";
import { useAlertDialog } from "../../hooks/useAlertDialog.tsx";
import {
  getReplenishOrder,
  saveReplenishOrder,
  setReplenishOrderFinished,
  setReplenishOrderDeleted,
  type ReplenishOrder,
} from "../../api/replenish.ts";
import formStyles from "../../styles/formPage.module.scss";

interface Status { type: "success" | "error" | "warning" | "info"; message: string }

/**
 * Edit-Replenish-Order body, shared by the standalone {@link EditReplenishPage}
 * and the modal/popup wrapper {@link EditReplenishModal}. Loads the order
 * on mount, edits Identifier+Note, and exposes Finish/Un-Finish/Delete actions
 * in footerLeft. The line-items table is read-only — items are stocked by
 * the kiosk app, not edited here.
 */
function EditReplenishBody({
  orderId,
  onClose,
  onSaved,
}: {
  orderId: number;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const { toast } = useToast();
  const { confirm } = useAlertDialog();
  const [order, setOrder] = useState<ReplenishOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);

  const lastLoadKeyRef = useRef<number>(Number.NaN);
  useEffect(() => {
    if (lastLoadKeyRef.current === orderId) return;
    lastLoadKeyRef.current = orderId;
    if (!orderId) {
      setStatus({ type: "error", message: "Missing order ID." });
      setLoading(false);
      return;
    }
    setLoading(true);
    getReplenishOrder(orderId)
      .then((r) => {
        if (r.bError || !r.oData) {
          setStatus({ type: "error", message: r.sMessage || "Failed to load order." });
          return;
        }
        setOrder(r.oData);
      })
      .catch((err) => setStatus({ type: "error", message: String(err) }))
      .finally(() => setLoading(false));
  }, [orderId]);

  const handleSave = useCallback(async () => {
    if (!order) return;
    setSaving(true);
    setStatus(null);
    try {
      const r = await saveReplenishOrder(order);
      if (r.bError || !r.oData) {
        setStatus({ type: "error", message: r.sMessage || "Failed to save." });
        return;
      }
      toast("Saved.", "success");
      onSaved?.();
      onClose();
    } catch (err) {
      setStatus({ type: "error", message: String(err) });
    } finally {
      setSaving(false);
    }
  }, [order, toast, onSaved, onClose]);

  const setFinished = useCallback(async (finished: boolean) => {
    if (!order) return;
    const ok = await confirm({
      title: "Confirmation",
      body: `Are you sure you wish to set this to ${finished ? "finished" : "un-finished"}?`,
      confirmLabel: "Yes",
    });
    if (!ok) return;
    setSaving(true);
    setStatus(null);
    try {
      const r = await setReplenishOrderFinished(order.ID, finished);
      if (r.bError || !r.oData) {
        setStatus({ type: "error", message: r.sMessage || "Failed to update." });
        return;
      }
      toast("Action completed.", "success");
      onSaved?.();
      onClose();
    } catch (err) {
      setStatus({ type: "error", message: String(err) });
    } finally {
      setSaving(false);
    }
  }, [order, confirm, toast, onSaved, onClose]);

  const handleDelete = useCallback(async () => {
    if (!order) return;
    const ok = await confirm({
      title: "Delete order?",
      body: "This order will be marked deleted.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    setSaving(true);
    setStatus(null);
    try {
      const r = await setReplenishOrderDeleted(order.ID);
      if (r.bError || !r.oData) {
        setStatus({ type: "error", message: r.sMessage || "Failed to delete." });
        return;
      }
      toast("Order deleted.", "success");
      onSaved?.();
      onClose();
    } catch (err) {
      setStatus({ type: "error", message: String(err) });
    } finally {
      setSaving(false);
    }
  }, [order, confirm, toast, onSaved, onClose]);

  const footerLeft = order ? (
    <>
      {order.Finished == null ? (
        <button
          type="button"
          className={formStyles.secondaryBtn}
          onClick={() => setFinished(true)}
          disabled={saving}
        >
          Finish
        </button>
      ) : (
        <button
          type="button"
          className={formStyles.secondaryBtn}
          onClick={() => setFinished(false)}
          disabled={saving}
        >
          Un-Finish
        </button>
      )}
      <button
        type="button"
        className={formStyles.secondaryBtn}
        onClick={handleDelete}
        disabled={saving}
      >
        Delete
      </button>
    </>
  ) : null;

  return (
    <GridEditModal
      title={`Edit Replenish Order #${orderId}`}
      saving={saving}
      loading={loading}
      statusMessage={status}
      saveLabel="Save"
      footerLeft={footerLeft}
      onClose={onClose}
      onSave={handleSave}
    >
      {order && (
        <div className={formStyles.formContainer}>
          <div className={formStyles.fieldRow}>
            <label>Identifier</label>
            <input
              className={formStyles.fieldInput}
              value={order.Identifier ?? ""}
              onChange={(e) => setOrder({ ...order, Identifier: e.target.value })}
            />
          </div>
          <div className={formStyles.fieldRow}>
            <label>Note</label>
            <textarea
              className={formStyles.fieldInput}
              rows={4}
              value={order.Note ?? ""}
              onChange={(e) => setOrder({ ...order, Note: e.target.value })}
            />
          </div>

          <div style={{ marginTop: "var(--space-3)" }}>
            <h4 style={{ margin: "0 0 var(--space-2) 0" }}>Items</h4>
            <div style={{ overflowX: "auto", maxWidth: "100%" }}>
            <table className={formStyles.dataTable} style={{ tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: 100 }} />
                <col />
                <col style={{ width: 100 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 90 }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ textAlign: "right" }}>Product ID</th>
                  <th style={{ textAlign: "left" }}>Name</th>
                  <th style={{ textAlign: "right" }}>To Stock</th>
                  <th style={{ textAlign: "right" }}>Stocked</th>
                  <th style={{ textAlign: "left" }}>Finished</th>
                </tr>
              </thead>
              <tbody>
                {order.Items.length === 0 ? (
                  <tr><td colSpan={5} className={formStyles.emptyRow}>No items.</td></tr>
                ) : (
                  order.Items.map((item) => (
                    <tr key={item.ID}>
                      <td style={{ textAlign: "right" }}>{item.ProductID}</td>
                      <td style={{ textAlign: "left" }}>{item.Name}</td>
                      <td style={{ textAlign: "right" }}>{item.ToStock}</td>
                      <td style={{ textAlign: "right" }}>{item.Stocked ?? ""}</td>
                      <td style={{ textAlign: "left" }}>{item.Finished == null ? "NO" : "YES"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}
    </GridEditModal>
  );
}

/** Popup wrapper — used by `pagePopup.tsx` when routed via `?id=`. */
export function EditReplenishModal({
  popupEditId,
  onClose,
  onSaved,
}: {
  popupEditId: string | undefined;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const orderId = parseInt(popupEditId ?? "0", 10);
  return <EditReplenishBody orderId={orderId} onClose={onClose} onSaved={onSaved} />;
}

/** Standalone page route — reads `?id=` from the URL. */
export function EditReplenishPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const orderId = parseInt(params.get("id") ?? "0", 10);

  return (
    <SimplePageLayout title="Edit Replenish Order">
      <EditReplenishBody
        orderId={orderId}
        onClose={() => navigate(-1)}
      />
    </SimplePageLayout>
  );
}
